import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { enumDutiesCalculationStatus } from '@app/data-layer/enums/common';
import { DutiesCalculationResponseModel } from '@app/data-layer/models/common/duties-calculation-response.model';
import { FavoriteHtsNumberModel } from '@app/data-layer/models/common/favorite-hts-number.model';
import { BinaRepository, CodeRepository } from '@app/data-layer/repositories';
import { CHINA_COUNTRY_ID } from '@constants/constants';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { INPUT_TYPES_MAP } from '@constants/input-types-map';
import { enumBinaCatalogItemType } from '@enum/enumBinaCatalogItemType';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@services/alert.service';
import { omit } from 'lodash-es';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-bina-catalog-calculator-sidebar',
  templateUrl: './bina-catalog-calculator-sidebar.component.html',
  styleUrls: ['./bina-catalog-calculator-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BinaCatalogCalculatorSidbarComponent implements OnInit, OnDestroy {
  @Input() public favoriteHtsNumber: FavoriteHtsNumberModel;
  @Output() public close: EventEmitter<void> = new EventEmitter<void>();

  public calculatorForm: UntypedFormGroup;
  public enumBinaCatalogItemType: typeof enumBinaCatalogItemType = enumBinaCatalogItemType;
  public dutiesCalculation: DutiesCalculationResponseModel;
  public CUSTOM_MASK: typeof CUSTOM_MASK = CUSTOM_MASK;
  public DEFAULT_CURRENCY_CODE: string = DEFAULT_CURRENCY_CODE;
  public CHINA_COUNTRY_ID: number = CHINA_COUNTRY_ID;
  public DECIMAL_INPUT_MAP = INPUT_TYPES_MAP.DECIMAL;
  public isShowChinaDisclaimer: boolean = false;
  public isShowOnlySpecialTariffRate: boolean = false;
  public specialTariffRateTranslationText: string;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private translateService: TranslateService,
    private binaRepository: BinaRepository,
    private codeRepository: CodeRepository,
    private alertService: AlertService,
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.initCalculatorForm();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public searchCountry() {
    return this.codeRepository.searchCountry.bind(this.codeRepository);
  }

  public onCalculate() {
    const formData = this.showQuantityField ? this.calculatorForm.value : omit(this.calculatorForm.value, ['quantity1']);
    this.binaRepository
      .getPreliminaryDuties(
        {
          ...formData,
          htsNumber: this.favoriteHtsNumber.htsNumber,
          estimatedGoodsValue: parseInt(this.calculatorForm.get('estimatedGoodsValue').value),
        },
        true,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        const status = response.status;

        // #11230
        if (status !== enumDutiesCalculationStatus.Success) {
          const translatedErrorMessage = this.translateService.instant('BINA_CATALOG.CALCULATION_ERROR_MESSAGE');
          this.alertService.modalInfo(translatedErrorMessage);
        } else {
          this.dutiesCalculation = response;
          this.isShowChinaDisclaimer = this.countryOfOriginId === CHINA_COUNTRY_ID && !this.dutiesCalculation?.specialTariffRate;
          this.isShowOnlySpecialTariffRate =
            this.dutiesCalculation?.specialTariffRate !== null && this.countryOfOriginId !== CHINA_COUNTRY_ID;
          this.specialTariffRateTranslationText =
            this.countryOfOriginId === CHINA_COUNTRY_ID ? 'LABELS.SPECIAL_TARIFFS_RATE' : 'LABELS.TRADE_AGREEMENT_RATE';
          this.calculatorForm.markAsPristine();
          this.cdr.markForCheck();
        }
      });
  }

  public get countryOfOriginId(): number {
    return this.calculatorForm.value?.countryOfOriginId;
  }

  public get isCalculateDisabled(): boolean {
    return this.calculatorForm.pristine || !this.calculatorForm.valid;
  }

  public get showEstimatedDutiesAndTariffs(): boolean {
    return !!this.dutiesCalculation && this.dutiesCalculation?.estimatedDutiesAmount >= 0;
  }

  public get showEstimatedDutiesAndTariffsTotal(): boolean {
    return !this.isShowOnlySpecialTariffRate && this.showSpecialTariffRate;
  }

  public get estimatedDutiesAndTariffsTotal(): number {
    return this.dutiesCalculation?.estimatedDutiesAmount + this.dutiesCalculation?.specialTariffDutiesAmount;
  }

  public get showSpecialTariffRate(): boolean {
    return this.dutiesCalculation?.specialTariffRate !== null;
  }

  public get showQuantityField(): boolean {
    //#11282
    return this.favoriteHtsNumber?.dutyComputationCode === '1' || this.favoriteHtsNumber?.dutyComputationCode === '4';
  }

  private initCalculatorForm(): void {
    const quantity1Control = this.showQuantityField ? new UntypedFormControl(null, Validators.required) : new UntypedFormControl(null);
    this.calculatorForm = this.formBuilder.group({
      countryOfOriginId: new UntypedFormControl(null, Validators.required),
      estimatedGoodsValue: new UntypedFormControl(null, Validators.required),
      quantity1: quantity1Control,
    });
  }
}
